import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import {
  StyledAdvancedSearch,
  StyledSubtitle,
} from 'pages/AncillariesPage/AncillariesReferenceTablePage/AncillariesReferenceAccesoriessSearchForm/styles';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { useTranslations } from 'hooks/useTranslations';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { FormInput } from 'components/molecules/FormInput';
import { getFieldsByAmmunitionType } from 'pages/AncillariesPage/helpers';
import { EArtAmmunitionType, EArtAncillaryType } from 'constants/ArtAccessoryType';
import { ESearchFilterKeys } from 'constants/SearchFilterTypes';
import { prepareFilters } from 'helpers/filters';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { useLocation } from 'react-router-dom';
import { replaceUrlIfOnPage } from 'utils/history';
import { objectToQueryString } from 'helpers/searchQuery';
import { EPerPages } from 'models/table';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { FormAsyncMultiAutocomplete } from 'components/molecules/FormAsyncMultiAutocomplete';

export enum ESearchFormLabels {
  ammunitionTypeUniqueId = 'ammunitionTable.accordion.ammunitionType.label',
  manufacturersUniqueIds = 'ammunitionTable.accordion.ammunitionManufacturer.label',
  productName = 'ammunitionTable.accordion.productName.label',
  factoriesUniqueIds = 'ammunitionTable.accordion.factory.label',
  calibresUniqueIds = 'ammunitionTable.accordion.calibre.label',
  cartridgeCompositionsUniqueIds = 'ammunitionTable.accordion.catridgeComposition.label',
  coloursOfPrimerSealantUniqueIds = 'ammunitionTable.accordion.colourOfPrimerSealant.label',
  projectileShapesUniqueIds = 'ammunitionTable.accordion.projectileShape.label',
  functionalTypesUniqueIds = 'ammunitionTable.accordion.functionalType.label',
  shotSizesUniqueIds = 'ammunitionTable.accordion.shotSize.label',
  munitionTypesUniqueIds = 'ammunitionTable.accordion.munitionType.label',
  munitionVelocitiesUniqueIds = 'ammunitionTable.accordion.munitionVelocity.label',
  alphanumericalMarkings = 'ammunitionTable.accordion.alphanumericalMarkins.label',
}

export interface IAmmunitionSearchFormValues {
  ammunitionTypeUniqueId: string;
  manufacturersUniqueIds: string[];
  productName: string;
  factoriesUniqueIds: string[];
  calibresUniqueIds: string[];
  cartridgeCompositionsUniqueIds: string[];
  coloursOfPrimerSealantUniqueIds: string[];
  projectileShapesUniqueIds: string[];
  functionalTypesUniqueIds: string[];
  munitionTypesUniqueIds: string[];
  shotSizesUniqueIds: string[];
  munitionVelocitiesUniqueIds: string[];
  alphanumericalMarkings: string;
  notes?: string;
  invokedByPageChange?: boolean;
}

export const initialFormValues: IAmmunitionSearchFormValues = {
  ammunitionTypeUniqueId: '',
  manufacturersUniqueIds: [],
  productName: '',
  factoriesUniqueIds: [],
  calibresUniqueIds: [],
  cartridgeCompositionsUniqueIds: [],
  coloursOfPrimerSealantUniqueIds: [],
  projectileShapesUniqueIds: [],
  functionalTypesUniqueIds: [],
  munitionTypesUniqueIds: [],
  shotSizesUniqueIds: [],
  munitionVelocitiesUniqueIds: [],
  alphanumericalMarkings: '',
};

interface IAncillariesReferenceAmmunitionSearchForm {
  onSubmit: (arg: any) => void;
  initialValues: IAmmunitionSearchFormValues;
  isTransactionPage?: boolean;
  type?: string | null;
  setIsQueryAncillariesReferenceLoading: (arg: any) => void;
  isSearchButtonDisabled?: boolean;
  currentPage?: number;
  perPage?: EPerPages;
}

const AncillariesReferenceAmmunitionSearchForm = ({
  onSubmit,
  initialValues,
  isTransactionPage,
  type = null,
  isSearchButtonDisabled = false,
  setIsQueryAncillariesReferenceLoading,
  perPage,
  currentPage,
}: IAncillariesReferenceAmmunitionSearchForm) => {
  const { pathname }: any = useLocation();
  const { t } = useTranslations();
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const [values, setValues] = useState<IAmmunitionSearchFormValues>(initialValues);
  const [isLoadingDictionaries, setIsLoadingDictionaries] = useState<boolean>(false);
  const [isInitialFormLoad, setIsInitialFormLoad] = useState(true);
  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = useForm<IAmmunitionSearchFormValues>({
    defaultValues: initialValues,
  });

  const { selectedLanguage } = useLang();
  const {
    loadDictionaries,
    artAmmunitionType,
    artAmmunitionManufacturer,
    artAmmunitionFactory,
    artAmmunitionCalibreGauge,
    artAmmunitionCartridgeComposition,
    artAmmunitionColourOfPrimerSealant,
    artAmmunitionProjectileShape,
    artAmmunitionFunctionalType,
    artAmmunitionShotSize,
    artAmmunitionMunitionType,
    artAmmunitionMunitionVelocity,
  } = useAncillaries();

  useEffect(() => {
    (async function initDictionaries() {
      setIsLoadingDictionaries(true);
      await loadDictionaries([
        EDictionaryTypes.ArtAmmunitionType,
        EDictionaryTypes.ArtAmmunitionManufacturer,
        EDictionaryTypes.ArtAmmunitionFactory,
        EDictionaryTypes.ArtAmmunitionCalibreGauge,
        EDictionaryTypes.ArtAmmunitionCartridgeComposition,
        EDictionaryTypes.ArtAmmunitionColourOfPrimerSealant,
        EDictionaryTypes.ArtAmmunitionProjectileShape,
        EDictionaryTypes.ArtAmmunitionFunctionalType,
        EDictionaryTypes.ArtAmmunitionShotSize,
        EDictionaryTypes.ArtAmmunitionMunitionType,
        EDictionaryTypes.ArtAmmunitionMunitionVelocity,
      ]);
      setIsLoadingDictionaries(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as IAmmunitionSearchFormValues);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  useEffect(() => {
    if (!isLoadingDictionaries) {
      setValues(initialValues);
      reset(initialValues);
      handleSubmit(onSubmit)();
    }
  }, [initialValues, isLoadingDictionaries]);

  const ammunitionTypeChange = (value: EArtAmmunitionType | string) => {
    reset({ ...initialValues, ammunitionTypeUniqueId: value });
    if (value) {
      const fields = getFieldsByAmmunitionType(value as EArtAmmunitionType);
      setAccessFields(fields);
    } else {
      setAccessFields([]);
    }
  };

  useEffect(() => {
    if (initialValues.ammunitionTypeUniqueId) {
      ammunitionTypeChange(initialValues.ammunitionTypeUniqueId);
    }
  }, [initialValues]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name && name !== 'ammunitionTypeUniqueId') {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialValues);
      ammunitionTypeChange('');
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  const checkDisabled = (fieldName: string) => !accessFields.includes(fieldName);

  const submitForm = (data: IAmmunitionSearchFormValues) => {
    if (data.invokedByPageChange !== true) {
      setIsQueryAncillariesReferenceLoading(true);
    }
    data.invokedByPageChange = undefined;

    if (isTransactionPage) {
      const query = objectToQueryString(data);
      if (query.length) {
        replaceUrlIfOnPage(
          `${pathname}?type=${type}&expand=ancillaries&ancillaryOptionName=${EArtAncillaryType.AMMUNITION}&${query}`,
        );
      } else {
        replaceUrlIfOnPage(
          `${pathname}?type=${type}&expand=ancillaries&ancillaryOptionName=${EArtAncillaryType.AMMUNITION}`,
        );
      }
    } else {
      const query = objectToQueryString({
        ...data,
        perPage,
        currentPage,
      });
      if (query.length) {
        replaceUrlIfOnPage(
          `${pathname}?expand=ancillaries-reference-table&ancillaryOptionName=${EArtAncillaryType.AMMUNITION}&${query}`,
        );
      } else {
        replaceUrlIfOnPage(
          `${pathname}?expand=ancillaries-reference-table&ancillaryOptionName=${EArtAncillaryType.AMMUNITION}`,
        );
      }
    }

    onSubmit(data);
  };

  useEffect(() => {
    if (!isInitialFormLoad) {
      handleSubmit((val) => submitForm({ ...val, invokedByPageChange: true }))();
    } else {
      setIsInitialFormLoad(false);
    }
  }, [perPage, currentPage]);

  return (
    <SearchWrapper>
      <form onSubmit={handleSubmit(submitForm)}>
        <StyledAdvancedSearch container columnSpacing={2} columns={10}>
          <Grid size={{ xs: 12 }}>
            <StyledSubtitle size={{ xs: 3 }}>
              <Typography variant="subtitle2">
                {t('ancillaries.accordion.subtitle.label')}
              </Typography>
            </StyledSubtitle>
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(artAmmunitionType)}
              name="ammunitionTypeUniqueId"
              label={t(ESearchFormLabels.ammunitionTypeUniqueId)}
              setValue={(name: string, value: string) => {
                // @ts-expect-error: required by update
                setValue(name, value);
                ammunitionTypeChange(value as EArtAmmunitionType | '');
              }}
              canBeEmpty
              control={control}
              errors={errors}
              onSelect={ammunitionTypeChange}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAmmunitionManufacturer}
              name="manufacturersUniqueIds"
              label={t(ESearchFormLabels.manufacturersUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('manufacturersUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="productName"
              label={t(ESearchFormLabels.productName)}
              control={control}
              errors={errors}
              disabled={checkDisabled('productName')}
              setValue={setValue}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAmmunitionFactory}
              name="factoriesUniqueIds"
              label={t(ESearchFormLabels.factoriesUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('factoriesUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAmmunitionCalibreGauge}
              name="calibresUniqueIds"
              label={t(ESearchFormLabels.calibresUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('calibresUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAmmunitionCartridgeComposition}
              name="cartridgeCompositionsUniqueIds"
              label={t(ESearchFormLabels.cartridgeCompositionsUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('cartridgeCompositionsUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAmmunitionColourOfPrimerSealant}
              name="coloursOfPrimerSealantUniqueIds"
              label={t(ESearchFormLabels.coloursOfPrimerSealantUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('coloursOfPrimerSealantUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAmmunitionProjectileShape}
              name="projectileShapesUniqueIds"
              label={t(ESearchFormLabels.projectileShapesUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('projectileShapesUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAmmunitionFunctionalType}
              name="functionalTypesUniqueIds"
              label={t(ESearchFormLabels.functionalTypesUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('functionalTypesUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAmmunitionShotSize}
              name="shotSizesUniqueIds"
              label={t(ESearchFormLabels.shotSizesUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('shotSizesUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(artAmmunitionMunitionType)}
              name="munitionTypesUniqueIds"
              label={t(ESearchFormLabels.munitionTypesUniqueIds)}
              setValue={setValue}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('munitionVelocitiesUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(artAmmunitionMunitionVelocity)}
              name="munitionVelocitiesUniqueIds"
              label={t(ESearchFormLabels.munitionVelocitiesUniqueIds)}
              setValue={setValue}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('munitionTypesUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="alphanumericalMarkings"
              label={t(ESearchFormLabels.alphanumericalMarkings)}
              control={control}
              errors={errors}
              disabled={checkDisabled('alphanumericalMarkings')}
              setValue={setValue}
            />
          </Grid>

          <Grid
            size={{ xs: 12 }}
            container
            columns={10}
            columnSpacing={2}
            sx={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Grid size={{ xs: 10, sm: 10, md: 5, lg: 2 }}>
              <Button
                variant={EButtonVariants.contained}
                size={EButtonSizes.small}
                id="ancillary-reference-table-ammunition-search"
                label={t('ancillaries.accordion.search.button')}
                disabled={isSearchButtonDisabled}
                type="submit"
                fullWidth
                sx={{
                  mt: {
                    xs: 2,
                    sm: 3.5,
                  },
                }}
              />
            </Grid>
          </Grid>
        </StyledAdvancedSearch>
      </form>
      <SelectedFilters
        name="ancillaries_reference_ammunition"
        values={values}
        labels={ESearchFormLabels}
        handleDelete={handleResetField}
        type={ESearchFilterKeys.ancillaries}
        showSaveFilters={false}
        saveQueryParams={['type', 'ancillaryOptionName']}
        dictionaries={{
          ammunitionTypeUniqueId: prepareFilters(artAmmunitionType, 'uniqueId', 'name'),
          manufacturersUniqueIds: prepareFilters(
            artAmmunitionManufacturer,
            'uniqueId',
            'name',
          ),
          factoriesUniqueIds: prepareFilters(artAmmunitionFactory, 'uniqueId', 'name'),
          calibresUniqueIds: prepareFilters(artAmmunitionCalibreGauge, 'uniqueId', 'name'),
          cartridgeCompositionsUniqueIds: prepareFilters(
            artAmmunitionCartridgeComposition,
            'uniqueId',
            'name',
          ),
          coloursOfPrimerSealantUniqueIds: prepareFilters(
            artAmmunitionColourOfPrimerSealant,
            'uniqueId',
            'name',
          ),
          projectileShapesUniqueIds: prepareFilters(
            artAmmunitionProjectileShape,
            'uniqueId',
            'name',
          ),
          functionalTypesUniqueIds: prepareFilters(
            artAmmunitionFunctionalType,
            'uniqueId',
            'name',
          ),
          munitionVelocitiesUniqueIds: prepareFilters(
            artAmmunitionMunitionVelocity,
            'uniqueId',
            'name',
          ),
          shotSizesUniqueIds: prepareFilters(artAmmunitionShotSize, 'uniqueId', 'name'),
          munitionTypesUniqueIds: prepareFilters(
            artAmmunitionMunitionType,
            'uniqueId',
            'name',
          ),
        }}
        saveRouterStateAfterClearAll={false}
      />
    </SearchWrapper>
  );
};

export default AncillariesReferenceAmmunitionSearchForm;
